import { CalendarPlus, Cloud, Server } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { Callout } from '@/components/patterns/callout';
import { DataTable, Paginacao, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterFlagsInstalacao, FilterLimpar, FilterSelect } from '@/components/patterns/filter-bar';
import { DetalheItem, DetalheLista } from '@/components/patterns/metric';
import { PermissionButton } from '@/components/patterns/permission-button';
import { AdministradorBadge, InstalacaoFlags, PapelBadge, SaudeBadge } from '@/components/patterns/status-badge';
import { Card, CardContent, CardEyebrow, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { dataSource } from '@/data';
import type { Instalacao } from '@/data/types';
import { BREAKPOINT_TABLET, useMediaQuery } from '@/hooks/use-media-query';
import { usePaginacao } from '@/hooks/use-paginacao';
import { useResource } from '@/hooks/use-resource';
import { formatarDataHora, plural, texto } from '@/lib/format';
import { estaDesatualizada, flagDoFiltro, ordenarPorFamilia, podeAgendar } from '@/lib/rules';
import { cn } from '@/lib/utils';

/**
 * Versão e saúde — master-detail.
 *
 * O app original resolvia isto com um grid de 6 colunas e um painel fixo de 40%
 * à direita, sempre visível mesmo sem seleção. Aqui o detalhe só existe quando
 * há instalação selecionada (fora disso, a lista ocupa a tela inteira) e no
 * tablet/mobile ele vira Sheet — a mesma informação, sem espremer a tabela.
 */
export function VersaoSaudePage() {
  const navegar = useNavigate();
  const compacto = useMediaQuery(BREAKPOINT_TABLET);

  const carregar = useCallback((signal: AbortSignal) => dataSource.listInstalacoes(signal), []);
  const recurso = useResource<Instalacao[]>(carregar);

  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [saude, setSaude] = useState<string | null>(null);
  const [papel, setPapel] = useState<string | null>(null);
  const [ana, setAna] = useState<string | null>(null);
  const [interno, setInterno] = useState<string | null>(null);

  const instalacoes = useMemo(() => ordenarPorFamilia(recurso.dados ?? []), [recurso.dados]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return instalacoes.filter((item) => {
      if (saude && item.saude !== saude) return false;
      if (papel && item.papel !== papel) return false;
      if (flagDoFiltro(ana) !== undefined && item.ana !== flagDoFiltro(ana)) return false;
      if (flagDoFiltro(interno) !== undefined && item.interno !== flagDoFiltro(interno)) return false;
      if (!termo) return true;
      return `${item.nome} ${item.codigo ?? ''} ${item.cliente?.nome ?? ''} ${item.versaoAtual}`.toLowerCase().includes(termo);
    });
  }, [instalacoes, busca, saude, papel, ana, interno]);

  const selecionada = instalacoes.find((item) => item.id === selecionadaId) ?? null;
  const filtrosAtivos = [busca.trim() !== '', saude !== null, papel !== null, ana !== null, interno !== null].filter(Boolean).length;

  const paginacao = usePaginacao(filtradas, `${busca}|${saude}|${papel}|${ana}|${interno}`);

  const colunas: Array<Coluna<Instalacao>> = [
    {
      id: 'nome',
      cabecalho: 'Instalação',
      ordenarPor: (item) => item.nome,
      celula: (item) => (
        <div className={cn('flex items-center gap-3', item.controladoraId != null && 'pl-4')}>
          {item.tipo === 'Cloud' ? (
            <Cloud className="size-4 shrink-0 text-icon" aria-label="Cloud" />
          ) : (
            <Server className="size-4 shrink-0 text-icon" aria-label="On-premises" />
          )}
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-3">
              <span className="truncate font-bold">{item.nome}</span>
              <InstalacaoFlags ana={item.ana} interno={item.interno} />
            </span>
            <span className="truncate text-label font-normal text-on-surface-muted">
              {texto(item.cliente?.nome, 'Cliente sem dado')}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'codigo',
      cabecalho: 'Código',
      largura: '150px',
      ordenarPor: (item) => item.codigo ?? '',
      celula: (item) => <span className="font-narrow text-cell text-on-surface-muted">{texto(item.codigo)}</span>,
    },
    { id: 'papel', cabecalho: 'Papel', largura: '170px', celula: (item) => <PapelBadge valor={item.papel} /> },
    {
      id: 'versao',
      cabecalho: 'Versão atual',
      largura: '150px',
      ordenarPor: (item) => item.versaoAtual,
      celula: (item) => <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(item.versaoAtual)}</span>,
    },
    {
      id: 'recomendada',
      cabecalho: 'Recomendada',
      largura: '170px',
      secundaria: true,
      celula: (item) =>
        estaDesatualizada(item) ? (
          <span className="font-narrow text-[15px] font-bold tabular-nums text-warning-text">
            {texto(item.versaoEstavelRecomendada)}
          </span>
        ) : (
          <span className="text-label font-normal text-on-surface-muted">Em dia</span>
        ),
    },
    { id: 'saude', cabecalho: 'Saúde', largura: '150px', celula: (item) => <SaudeBadge valor={item.saude} /> },
    {
      id: 'administrador',
      cabecalho: 'Permissão',
      largura: '180px',
      secundaria: true,
      celula: (item) => <AdministradorBadge valor={item.administrador} />,
    },
  ];

  const detalhe = selecionada ? <DetalheInstalacao instalacao={selecionada} aoAgendar={() => navegar('/agendar')} /> : null;

  return (
    <PageBody>
      <PageHeader
        eyebrow="Parque instalado"
        titulo="Versão e saúde"
        descricao="Versão aplicada, versão estável recomendada e estado operacional de cada instalação do escopo."
      />

      <div className={selecionada && !compacto ? 'grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_340px]' : ''}>
        <TabelaPainel
          titulo="Instalações"
          contagem={recurso.estado === 'pronto' ? plural(filtradas.length, 'resultado', 'resultados') : undefined}
          filtros={
            <FilterBar>
        <FilterBusca
          id="filtro-busca-instalacao"
          label="Buscar instalação"
          valor={busca}
          aoMudar={setBusca}
          placeholder="Nome ou código da instalação, cliente ou versão"
        />
        <FilterSelect
          id="filtro-saude"
          label="Saúde"
          valor={saude}
          aoMudar={setSaude}
          rotuloTodos="Qualquer estado"
          opcoes={[
            { valor: 'OK', rotulo: 'Saudável' },
            { valor: 'Atencao', rotulo: 'Atenção' },
            { valor: 'Incidente', rotulo: 'Incidente' },
          ]}
        />
        <FilterSelect
          id="filtro-papel"
          label="Papel"
          valor={papel}
          aoMudar={setPapel}
          rotuloTodos="Qualquer papel"
          opcoes={[
            { valor: 'Controladora', rotulo: 'Controladora' },
            { valor: 'Controlada', rotulo: 'Controlada' },
            { valor: 'Instalação', rotulo: 'Instalação' },
          ]}
        />
        <FilterFlagsInstalacao
          idBase="filtro-instalacao"
          ana={ana}
          aoMudarAna={setAna}
          interno={interno}
          aoMudarInterno={setInterno}
          mostrarAna={instalacoes.some((item) => item.ana)}
          mostrarInterno={instalacoes.some((item) => item.interno)}
        />
        <FilterLimpar
          ativo={filtrosAtivos > 0}
          quantidade={filtrosAtivos}
          aoLimpar={() => {
            setBusca('');
            setSaude(null);
            setPapel(null);
            setAna(null);
            setInterno(null);
          }}
        />
            </FilterBar>
          }
          paginacao={
            recurso.estado === 'pronto' ? (
              <Paginacao
                pagina={paginacao.pagina}
                totalPaginas={paginacao.totalPaginas}
                aoMudar={paginacao.setPagina}
                tamanhoPagina={paginacao.tamanhoPagina}
                aoMudarTamanho={paginacao.setTamanhoPagina}
              />
            ) : undefined
          }
        >
            <DataTable
              rotulo="Instalações por versão e saúde"
              itens={paginacao.visiveis}
              colunas={colunas}
              chave={(item) => item.id}
              estado={recurso.estado}
              erro={recurso.erro}
              aoTentarDeNovo={recurso.recarregar}
              selecionado={selecionadaId}
              aoSelecionar={(item) => setSelecionadaId(item.id === selecionadaId && !compacto ? null : item.id)}
              vazio={
                filtrosAtivos > 0
                  ? {
                      titulo: 'Nenhuma instalação com esses filtros',
                      descricao: 'Ajuste a busca, a saúde ou o papel para ver outras instalações do escopo.',
                    }
                  : {
                      titulo: 'Nenhuma instalação no escopo',
                      descricao: 'A instalação de acesso deste usuário não tem instalações vinculadas.',
                    }
              }
            />
        </TabelaPainel>

        {selecionada && !compacto ? (
          <Card as="aside" className="h-fit xl:sticky xl:top-6">
            <CardHeader className="flex-col items-start gap-0.5">
              <CardEyebrow>Detalhe da instalação</CardEyebrow>
              <CardTitle>{selecionada.nome}</CardTitle>
            </CardHeader>
            <CardContent>{detalhe}</CardContent>
          </Card>
        ) : null}
      </div>

      {compacto ? (
        <Sheet open={Boolean(selecionada)} onOpenChange={(aberto) => !aberto && setSelecionadaId(null)}>
          <SheetContent>
            <SheetTitle>{selecionada?.nome ?? 'Detalhes'}</SheetTitle>
            {detalhe}
          </SheetContent>
        </Sheet>
      ) : null}
    </PageBody>
  );
}

function DetalheInstalacao({ instalacao, aoAgendar }: { instalacao: Instalacao; aoAgendar: () => void }) {
  const agendar = podeAgendar(instalacao);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <SaudeBadge valor={instalacao.saude} />
        <PapelBadge valor={instalacao.papel} />
        <AdministradorBadge valor={instalacao.administrador} />
      </div>

      <DetalheLista>
        <DetalheItem rotulo="Tipo">{texto(instalacao.tipo)}</DetalheItem>
        <DetalheItem rotulo="Região">
          {/* On-premises não tem região: dizer isso é diferente de mostrar vazio. */}
          {instalacao.tipo === 'On-premises' ? 'Não se aplica' : texto(instalacao.regiao)}
        </DetalheItem>
        <DetalheItem rotulo="Versão atual">
          <span className="font-narrow text-[15px] font-bold tabular-nums">{texto(instalacao.versaoAtual)}</span>
        </DetalheItem>
        <DetalheItem rotulo="Versão recomendada">
          <span className="font-narrow text-[15px] font-bold tabular-nums">
            {texto(instalacao.versaoEstavelRecomendada)}
          </span>
        </DetalheItem>
        <DetalheItem rotulo="Última atualização">{formatarDataHora(instalacao.ultimaAtualizacao)}</DetalheItem>
        <DetalheItem rotulo="Cloud code">{texto(instalacao.cloudCode, 'Não se aplica')}</DetalheItem>
      </DetalheLista>

      {estaDesatualizada(instalacao) ? (
        <Callout tom="alerta" kicker="Atualização pendente">
          Está na {instalacao.versaoAtual}; a versão estável recomendada é a {instalacao.versaoEstavelRecomendada}.
        </Callout>
      ) : (
        <Callout tom="info" kicker="Em dia">
          Já está na versão estável recomendada.
        </Callout>
      )}

      <PermissionButton
        variant="primary"
        permitido={agendar.permitido}
        motivo={agendar.motivo}
        onClick={aoAgendar}
        className="w-full"
      >
        <CalendarPlus aria-hidden="true" />
        Agendar atualização
      </PermissionButton>
    </div>
  );
}
